<script lang="ts">
    import { Input } from '@/components/ui/input';
    import { Button } from '@/components/ui/button';
    import { Label } from '@/components/ui/label';
    import Date from '@/components/Inputs/Date.svelte';
    import type { LeadFilters } from './kanban-filters.svelte';

    type Props = {
        filters: LeadFilters;
        onApply: () => void;
        onClear: () => void;
        onClose: () => void;
    };

    let { filters = $bindable(), onApply, onClear, onClose }: Props = $props();

    function handleApply() {
        onApply();
        onClose();
    }

    function handleClear() {
        onClear();
        onClose();
    }
</script>

<div class="advanced-filters">
    <h4 class="mb-4 font-medium">Advanced Filters</h4>

    <div class="filter-grid">
        <!-- Organisation -->
        <div class="filter-field">
            <Label for="filter-org">Organisation</Label>
            <Input
                id="filter-org"
                type="text"
                placeholder="Search organisation..."
                bind:value={filters.organisation_name}
            />
        </div>

        <!-- Email -->
        <div class="filter-field">
            <Label for="filter-email">Email</Label>
            <Input
                id="filter-email"
                type="text"
                placeholder="Search email..."
                bind:value={filters.email}
            />
        </div>

        <!-- Phone -->
        <div class="filter-field">
            <Label for="filter-phone">Phone</Label>
            <Input
                id="filter-phone"
                type="text"
                placeholder="Search phone..."
                bind:value={filters.phone}
            />
        </div>

        <!-- Postcode -->
        <div class="filter-field">
            <Label for="filter-postcode">Postcode</Label>
            <Input
                id="filter-postcode"
                type="text"
                placeholder="Search postcode..."
                bind:value={filters.postcode}
            />
        </div>

        <!-- Estimated Value Range -->
        <div class="filter-field">
            <Label>Estimated Value</Label>
            <div class="range-inputs">
                <Input
                    type="number"
                    placeholder="Min"
                    bind:value={filters.estimated_value_min}
                    min="0"
                />
                <span class="range-separator">-</span>
                <Input
                    type="number"
                    placeholder="Max"
                    bind:value={filters.estimated_value_max}
                    min="0"
                />
            </div>
        </div>

        <!-- Estimated Job Start Range -->
        <div class="filter-field">
            <Label>Est. Job Start</Label>
            <div class="date-range">
                <Date
                    label=""
                    bind:value={filters.estimated_job_start_from}
                />
                <span class="range-separator">to</span>
                <Date
                    label=""
                    bind:value={filters.estimated_job_start_to}
                />
            </div>
        </div>

        <!-- Estimated Job End Range -->
        <div class="filter-field">
            <Label>Est. Job End</Label>
            <div class="date-range">
                <Date
                    label=""
                    bind:value={filters.estimated_job_end_from}
                />
                <span class="range-separator">to</span>
                <Date
                    label=""
                    bind:value={filters.estimated_job_end_to}
                />
            </div>
        </div>

        <!-- Created At Range -->
        <div class="filter-field">
            <Label>Created Date</Label>
            <div class="date-range">
                <Date
                    label=""
                    bind:value={filters.created_at_from}
                />
                <span class="range-separator">to</span>
                <Date
                    label=""
                    bind:value={filters.created_at_to}
                />
            </div>
        </div>
    </div>

    <div class="filter-actions">
        <Button variant="outline" onclick={handleClear}>
            Clear All
        </Button>
        <Button onclick={handleApply}>
            Apply Filters
        </Button>
    </div>
</div>

<style>
    .advanced-filters {
        width: 420px;
        max-width: 100%;
        padding: 1rem;
        overflow: hidden;
    }

    .filter-grid {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .filter-field {
        display: flex;
        flex-direction: column;
        gap: 0.375rem;
    }

    .range-inputs {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .range-inputs :global(input) {
        flex: 1;
        min-width: 0;
    }

    .range-separator {
        color: var(--muted-foreground);
        font-size: 0.75rem;
        flex-shrink: 0;
    }

    .date-range {
        display: flex;
        align-items: center;
        gap: 0.25rem;
    }

    .date-range :global(.grid) {
        flex: 1;
        min-width: 0;
        gap: 0;
    }

    .date-range :global(.grid > label) {
        display: none;
    }

    .date-range :global(button) {
        width: 100%;
        padding-left: 0.5rem;
        padding-right: 0.5rem;
        font-size: 0.75rem;
    }

    .filter-actions {
        display: flex;
        justify-content: flex-end;
        gap: 0.5rem;
        margin-top: 1.5rem;
        padding-top: 1rem;
        border-top: 1px solid hsl(var(--border));
    }
</style>
